import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { Navigate } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useHasPageAccess } from '../masterLayout/MasterLayoutBody';
import { useModule } from '../utils/useModule';
import { getPaginatedAssets } from '../api/assetAPI';
import { getAdvancedAssetDepartments } from '../api/filtersAdvancedAssetsAPI';
import { getTasksForBudget } from '../api/componentsAPI';

const buildAssetLabel = (assetNumber, assetDescription) => {
  if (assetNumber && assetDescription) {
    return `${assetNumber} - ${assetDescription}`;
  }
  return assetNumber || assetDescription || 'Unnamed asset';
};

const formatExpenseLabel = (expense) => {
  if (!expense) {
    return null;
  }
  if (typeof expense === 'string' || typeof expense === 'number') {
    return String(expense);
  }
  const code = expense.code ? String(expense.code) : '';
  const name = expense.name ? String(expense.name) : '';
  if (code && name) {
    return `${code} - ${name}`;
  }
  if (name) {
    return name;
  }
  if (code) {
    return code;
  }
  if (expense.id !== undefined && expense.id !== null) {
    return `ID ${expense.id}`;
  }
  return null;
};

const getMonthValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const getDefaultStartMonth = () => getMonthValue(new Date());

const getDefaultEndMonth = () => {
  const today = new Date();
  const endMonth = new Date(today.getFullYear() + 1, today.getMonth() - 1, 1);
  return getMonthValue(endMonth);
};

const getMonthStartDate = (monthValue) => {
  if (!monthValue) return '';
  const [year, month] = monthValue.split('-').map(Number);
  if (!year || !month) return '';
  return `${year}-${String(month).padStart(2, '0')}-01`;
};

const getMonthEndDate = (monthValue) => {
  if (!monthValue) return '';
  const [year, month] = monthValue.split('-').map(Number);
  if (!year || !month) return '';
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
};

const BudgetingLayer = () => {
  const hasPageAccess = useHasPageAccess();
  const isLccModuleEnabled = useModule('lcc');

  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [departmentAssets, setDepartmentAssets] = useState({});
  const [assetLookup, setAssetLookup] = useState({});
  const [manualAssetIds, setManualAssetIds] = useState([]);
  const [excludedAssetIds, setExcludedAssetIds] = useState([]);

  const [startDate, setStartDate] = useState(() => getDefaultStartMonth());
  const [endDate, setEndDate] = useState(() => getDefaultEndMonth());
  const [period, setPeriod] = useState('month');
  const [grouping, setGrouping] = useState('asset');

  const [budgetData, setBudgetData] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [budgetError, setBudgetError] = useState('');
  const [formError, setFormError] = useState('');
  const [departmentsLoading, setDepartmentsLoading] = useState(false);

  const departmentNameMap = useMemo(() => {
    const map = {};
    departmentOptions.forEach((option) => {
      map[option.value] = option.label;
    });
    return map;
  }, [departmentOptions]);

  const addAssetsToLookup = useCallback((assets) => {
    if (!assets?.length) return;
    setAssetLookup((prev) => {
      const next = { ...prev };
      assets.forEach((asset) => {
        next[asset.value] = asset;
      });
      return next;
    });
  }, []);

  useEffect(() => {
    const loadDepartments = async () => {
      setDepartmentsLoading(true);
      try {
        const response = await getAdvancedAssetDepartments();
        const list = Array.isArray(response) ? response : response?.data || [];
        const options = list.map((dept) => ({
          value: dept.id,
          label: dept.name || `Department ${dept.id}`,
        }));
        setDepartmentOptions(options);
      } catch (error) {
        console.error('Failed to load departments', error);
      } finally {
        setDepartmentsLoading(false);
      }
    };

    loadDepartments();
  }, []);

  const loadDepartmentAssets = useCallback(
    async (departmentId) => {
      if (!departmentId || departmentAssets[departmentId]) {
        return;
      }
      try {
        const response = await getPaginatedAssets({
          page: 1,
          pageSize: 5000,
          assetDepartmentId: departmentId,
        });
        const list = response?.data || response || [];
        const options = list.map((asset) => ({
          value: asset.id,
          label: buildAssetLabel(asset.assetNumber, asset.assetDescription),
          departmentId: asset.assetDepartmentId ?? null,
          assetNumber: asset.assetNumber,
          assetDescription: asset.assetDescription,
        }));
        setDepartmentAssets((prev) => ({
          ...prev,
          [departmentId]: options,
        }));
        addAssetsToLookup(options);
      } catch (error) {
        console.error('Failed to load department assets', error);
      }
    },
    [addAssetsToLookup, departmentAssets],
  );

  const handleDepartmentChange = useCallback(
    (options) => {
      setSelectedDepartments((prev) => {
        const next = options || [];
        const prevIds = new Set(prev.map((option) => option.value));
        next.forEach((option) => {
          if (!prevIds.has(option.value)) {
            loadDepartmentAssets(option.value);
          }
        });
        return next;
      });
    },
    [loadDepartmentAssets],
  );

  const deptAssetIds = useMemo(() => {
    const ids = new Set();
    selectedDepartments.forEach((dept) => {
      const assets = departmentAssets[dept.value] || [];
      assets.forEach((asset) => ids.add(asset.value));
    });
    return ids;
  }, [departmentAssets, selectedDepartments]);

  useEffect(() => {
    setExcludedAssetIds((prev) => {
      const next = prev.filter((id) => deptAssetIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [deptAssetIds]);

  const selectedAssetIds = useMemo(() => {
    const selection = new Set(manualAssetIds);
    deptAssetIds.forEach((id) => {
      if (!excludedAssetIds.includes(id)) {
        selection.add(id);
      }
    });
    return Array.from(selection);
  }, [manualAssetIds, deptAssetIds, excludedAssetIds]);

  const selectedAssetOptions = useMemo(
    () => selectedAssetIds.map((id) => assetLookup[id]).filter(Boolean),
    [assetLookup, selectedAssetIds],
  );

  const handleAssetsChange = useCallback(
    (options) => {
      const nextOptions = options || [];
      const nextIds = new Set(nextOptions.map((opt) => opt.value));
      const currentIds = new Set(selectedAssetIds);
      const removed = [...currentIds].filter((id) => !nextIds.has(id));
      const added = [...nextIds].filter((id) => !currentIds.has(id));

      addAssetsToLookup(nextOptions);

      setManualAssetIds((prev) => {
        const next = new Set(prev);
        added.forEach((id) => {
          if (!deptAssetIds.has(id)) {
            next.add(id);
          }
        });
        removed.forEach((id) => next.delete(id));
        return Array.from(next);
      });

      setExcludedAssetIds((prev) => {
        const next = new Set(prev);
        removed.forEach((id) => {
          if (deptAssetIds.has(id)) {
            next.add(id);
          }
        });
        added.forEach((id) => next.delete(id));
        return Array.from(next);
      });
    },
    [addAssetsToLookup, deptAssetIds, selectedAssetIds],
  );

  const handleAssetSearch = useCallback(
    async (inputValue) => {
      try {
        const response = await getPaginatedAssets({
          page: 1,
          pageSize: 25,
          search: inputValue || '',
        });
        const list = response?.data || response || [];
        const options = list.map((asset) => ({
          value: asset.id,
          label: buildAssetLabel(asset.assetNumber, asset.assetDescription),
          departmentId: asset.assetDepartmentId ?? null,
          assetNumber: asset.assetNumber,
          assetDescription: asset.assetDescription,
        }));
        addAssetsToLookup(options);
        return options;
      } catch (error) {
        console.error('Asset search failed', error);
        return [];
      }
    },
    [addAssetsToLookup],
  );

  const isFormReady = Boolean(
    startDate && endDate && period && grouping && selectedAssetIds.length,
  );

  const handleShowBudget = useCallback(async () => {
    setFormError('');
    setBudgetError('');
    if (!isFormReady) {
      setFormError(
        'Select a start date, end date, budget period, grouping, and at least one asset.',
      );
      return;
    }

    setBudgetLoading(true);
    try {
      const startDateValue = getMonthStartDate(startDate);
      const endDateValue = getMonthEndDate(endDate);
      const response = await getTasksForBudget({
        assetIds: selectedAssetIds,
        startDate: startDateValue,
        endDate: endDateValue,
        period,
      });
      setBudgetData(response);
    } catch (error) {
      console.error('Failed to load budget data', error);
      setBudgetError('Unable to load budget data.');
    } finally {
      setBudgetLoading(false);
    }
  }, [endDate, isFormReady, period, selectedAssetIds, startDate]);

  const groupingHeader = useMemo(() => {
    if (grouping === 'department') return 'Department';
    if (grouping === 'cost-centre') return 'Cost Centre';
    if (grouping === 'expense-element') return 'Expense Element';
    if (grouping === 'task') return 'Task';
    return 'Asset';
  }, [grouping]);

  const budgetTable = useMemo(() => {
    if (!budgetData?.costElements?.length) {
      return null;
    }

    const periodNames = budgetData.costElements[0]?.periods?.map((p) => p.periodName) || [];
    const rowsByGroup = new Map();

    budgetData.costElements.forEach((element) => {
      const periods = element.periods || [];
      const assetLabel = buildAssetLabel(element.assetNumber, element.assetDescription);
      const taskLabel = [element.code.code, element.code.name].filter(Boolean).join(' - ') || element.name;
      const periodSample = periods[0];
      let groupLabel = assetLabel;

      if (grouping === 'department') {
        const assetMeta = assetLookup[element.assetId];
        const deptName = assetMeta?.departmentId
          ? departmentNameMap[assetMeta.departmentId]
          : null;
        groupLabel = deptName || 'Unassigned';
      }

      if (grouping === 'cost-centre') {
        groupLabel = element.costCentre || 'Unassigned';
      }

      if (grouping === 'task') {
        groupLabel = taskLabel || assetLabel;
      }

      if (grouping === 'expense-element') {
        const expenseGroups = [
          {
            label: formatExpenseLabel(periodSample?.partsExpense) || 'Unassigned',
            costKey: 'partsCost',
          },
          {
            label: formatExpenseLabel(periodSample?.labourExpense2) || 'Unassigned',
            costKey: 'labourCost',
          },
          {
            label: formatExpenseLabel(periodSample?.miscExpense) || 'Unassigned',
            costKey: 'miscCost',
          },
        ];

        expenseGroups.forEach(({ label, costKey }) => {
          const existing = rowsByGroup.get(label) || {
            label,
            totals: Array(periodNames.length).fill(0),
          };
          periods.forEach((periodEntry, idx) => {
            existing.totals[idx] += Number(periodEntry?.[costKey] || 0);
          });
          rowsByGroup.set(label, existing);
        });
        return;
      }

      const existing = rowsByGroup.get(groupLabel) || {
        label: groupLabel,
        totals: Array(periodNames.length).fill(0),
      };

      periods.forEach((periodEntry, idx) => {
        const parts = Number(periodEntry.partsCost || 0);
        const labour = Number(periodEntry.labourCost || 0);
        const misc = Number(periodEntry.miscCost || 0);
        existing.totals[idx] += parts + labour + misc;
      });

      rowsByGroup.set(groupLabel, existing);
    });

    const rows = Array.from(rowsByGroup.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
    const totalRow = {
      label: 'Total',
      totals: periodNames.map((_, idx) =>
        rows.reduce((sum, row) => sum + (row.totals[idx] || 0), 0),
      ),
    };

    return { periodNames, rows, totalRow };
  }, [assetLookup, budgetData, departmentNameMap, grouping]);

  const getBudgetExportRows = useCallback(() => {
    if (!budgetTable) return [];
    const rows = budgetTable.rows.map((row) => [row.label, ...row.totals]);
    return [...rows, [budgetTable.totalRow.label, ...budgetTable.totalRow.totals]];
  }, [budgetTable]);

  const handleExportExcel = useCallback(() => {
    if (!budgetTable) return;
    const headers = [groupingHeader, ...budgetTable.periodNames];
    const rows = getBudgetExportRows();
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Budget');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'budget.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [budgetTable, getBudgetExportRows, groupingHeader]);

  const handleExportCSV = useCallback(() => {
    if (!budgetTable) return;
    const headers = [groupingHeader, ...budgetTable.periodNames];
    const rows = getBudgetExportRows();
    const csvLines = [
      headers.join(','),
      ...rows.map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')),
    ];
    const csv = csvLines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'budget.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [budgetTable, getBudgetExportRows, groupingHeader]);

  if (!isLccModuleEnabled || !hasPageAccess('/budgeting')) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-body p-24">
        <div className="row g-3">
          <div className="col-xl-3 col-md-6">
            <label className="form-label">Start Month</label>
            <input
              type="month"
              className="form-control"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>
          <div className="col-xl-3 col-md-6">
            <label className="form-label">End Month</label>
            <input
              type="month"
              className="form-control"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>
          <div className="col-xl-3 col-md-6">
            <label className="form-label">Budget Period</label>
            <select
              className="form-select"
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
            >
              <option value="month">Months</option>
              <option value="quarter">Quarters</option>
              <option value="calendaryear">Calendar Year</option>
              <option value="financialyear">Financial Year</option>
            </select>
          </div>
          <div className="col-xl-3 col-md-6">
            <label className="form-label">Grouping</label>
            <select
              className="form-select"
              value={grouping}
              onChange={(event) => setGrouping(event.target.value)}
            >
              <option value="department">Department</option>
              <option value="asset">Assets</option>
              <option value="cost-centre">Cost Centre</option>
              <option value="expense-element">Expense Element</option>
              <option value="task">Task</option>
            </select>
          </div>
          <div className="col-12">
            <label className="form-label">Departments</label>
            <Select
              isMulti
              classNamePrefix="react-select"
              value={selectedDepartments}
              isLoading={departmentsLoading}
              onChange={handleDepartmentChange}
              options={departmentOptions}
              placeholder="Select departments"
            />
            <div className="form-text">Selecting a department adds all assets from that department.</div>
          </div>
          <div className="col-12">
            <label className="form-label">Assets</label>
            <AsyncSelect
              isMulti
              cacheOptions
              defaultOptions={selectedAssetOptions}
              classNamePrefix="react-select"
              value={selectedAssetOptions}
              loadOptions={handleAssetSearch}
              onChange={handleAssetsChange}
              placeholder="Search and select assets"
            />
            <div className="form-text">Remove assets here to exclude them from department selections.</div>
          </div>
        </div>

        {formError && <div className="text-danger mt-3">{formError}</div>}
        {budgetError && <div className="text-danger mt-3">{budgetError}</div>}

        <div className="d-flex justify-content-end align-items-center gap-2 mt-16 mb-16">
          <div className="dropdown">
            <button
              className="btn btn-outline-secondary text-sm px-12 py-12 radius-4 dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              disabled={!budgetTable}
            >
              Export Data
            </button>
            <ul className="dropdown-menu p-12 border bg-base shadow">
              <li>
                <button className="dropdown-item" onClick={handleExportExcel}>
                  Excel
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={handleExportCSV}>
                  CSV
                </button>
              </li>
            </ul>
          </div>
          <button
            className="btn btn-primary radius-4"
            type="button"
            onClick={handleShowBudget}
            disabled={budgetLoading}
          >
            {budgetLoading ? 'Loading…' : 'Load Budget'}
          </button>
        </div>

        <div className="mt-4">
          {budgetTable ? (
            <div
              className="table-responsive border rounded"
              style={{ maxHeight: '55vh', overflow: 'auto' }}
            >
              <table className="table table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ minWidth: 330 }}>{groupingHeader}</th>
                    {budgetTable.periodNames.map((periodName) => (
                      <th key={periodName} className="text-end" style={{ minWidth: 120 }}>
                        {periodName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {budgetTable.rows.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      {row.totals.map((value, index) => (
                        <td key={`${row.label}-${index}`} className="text-end">
                          {parseInt(value, 10).toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light">
                  <tr>
                    <th>{budgetTable.totalRow.label}</th>
                    {budgetTable.totalRow.totals.map((value, index) => (
                      <th key={`total-${index}`} className="text-end">
                        {parseInt(value, 10).toLocaleString()}
                      </th>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p className="text-secondary-light mb-0">
              Select filters and press &ldquo;Load Budget&rdquo; to view results.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BudgetingLayer;
